import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../../../../generic/hooks/useDetectOutsideClick'
import WarehouseIcon from '../../../../../generic/icons/IconLib/WarehouseIcon'
import WarehouseSelector from '../../../../../generic/ui/Selectors/WarehouseSelector'
import { FormShell, FormGrid, FormField } from '../../../../../generic/ui/FormLayout'
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList'

type Props = {
    warehouseList: IWarhouseListItem[]
    tranzitID: number
    handleSelectTransit: (id: number) => void
}

const LikutWarehouseFilterButton = ({
    warehouseList,
    tranzitID,
    handleSelectTransit,
}: Props) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = useState(false)

    useDetectOutsideClick(wrapperRef, () => setOpen(false))

    const selectedWarehouse = useMemo(
        () => warehouseList.find((w) => w.WarehouseID === tranzitID) ?? null,
        [warehouseList, tranzitID],
    )

    const handleSelect = (warehouse: IWarhouseListItem | null) => {
        handleSelectTransit(warehouse ? warehouse.WarehouseID : 0)
        setOpen(false)
    }

    return (
        <Wrapper ref={wrapperRef}>
            <Trigger
                type='button'
                aria-haspopup='dialog'
                aria-expanded={open}
                aria-label='בחירת מחסן'
                title='בחירת מחסן'
                $active={!!tranzitID}
                $open={open}
                onClick={() => setOpen((v) => !v)}
            >
                <WarehouseIcon />
                {!!tranzitID && <Count>1</Count>}
            </Trigger>

            {open && (
                <Panel role='dialog'>
                    <FormShell>
                        <FormGrid>
                            <FormField label='מחסן' fullWidth>
                                <WarehouseSelector
                                    warehouses={warehouseList}
                                    value={selectedWarehouse}
                                    onSelect={handleSelect}
                                    allowClear
                                    clearLabel='כל המחסנים'
                                />
                            </FormField>
                        </FormGrid>
                    </FormShell>
                </Panel>
            )}
        </Wrapper>
    )
}

export default LikutWarehouseFilterButton

const Wrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`

const Trigger = styled.button<{ $active: boolean; $open: boolean }>`
    position: relative;
    box-sizing: border-box;
    width: 2.875rem;
    height: 2.875rem;
    background: ${({ theme, $active }) => ($active ? theme.colors.accentSoft : theme.colors.surface)};
    border: 1px solid
        ${({ theme, $active, $open }) => ($open || $active ? theme.colors.ink : theme.colors.inputBorder)};
    border-radius: 0.875rem;
    color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.icon)};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1.25rem;
        height: 1.25rem;
    }
`

const Count = styled.span`
    position: absolute;
    top: -0.375rem;
    inset-inline-start: -0.375rem;
    min-width: 1.125rem;
    height: 1.125rem;
    padding: 0 0.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border-radius: 999px;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: ${({ theme }) => theme.text.xs};
    font-weight: 600;
`

const Panel = styled.div`
    position: absolute;
    top: calc(100% + 0.5rem);
    inset-inline-end: 0;
    z-index: 30;
    width: 18rem;
    max-width: calc(100vw - 1.5rem);
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: 0.875rem;
    direction: rtl;
`
